import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Modal } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAnimation } from '../../hooks/useAnimation';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import config from '../../config';
import './Careers.scss';

const CareersPage = () => {
  const { t } = useTranslation();
  const { fadeOnly, scaleIn } = useAnimation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // EmailJS'i component mount olduğunda başlat
  useEffect(() => {
    emailjs.init("ntUTrZ4oIBLhKCXjA"); // Public Key kullan
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');
    
    if (file) {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFileError(t('careers.form.file_size_error'));
        setSelectedFile(null);
        return;
      }
      
      // Dosya tipi kontrolü
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setFileError(t('careers.form.file_type_error'));
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // FormData oluştur
      const formDataToSend = new FormData();
      
      // Form verilerini ekle
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Dosya varsa ekle
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }
      
      // Backend'e kaydet
      const response = await axios.post(`${config.API_URL}/careers`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        // Email gönder
        try {
          // CV linkini oluştur
          let cvLink = '';
          if (response.data.file_path) {
            cvLink = `https://tekstas.com/api${response.data.file_path}`;
          }
          
          // EmailJS'i tekrar başlat (güvenlik için)
          emailjs.init("ntUTrZ4oIBLhKCXjA"); // Public Key kullan
          
          const emailResult = await emailjs.send(
            'service_hxxhqd6',
            'template_64s19y8',
            {
              from_name: formData.full_name,
              from_email: formData.email,
              phone: formData.phone,
              position: formData.position,
              message: formData.message,
              cv: cvLink
            },
            'ntUTrZ4oIBLhKCXjA' // Public Key kullan
          );
          
          console.log('Email sent successfully:', emailResult);
        } catch (emailError) {
          console.error('Email sending failed:', emailError);
          console.error('Error details:', {
            status: emailError.status,
            text: emailError.text,
            message: emailError.message
          });
          // Email gönderilemese bile form kaydedildi, kullanıcıya bilgi ver
        }
        
        // Backend zaten WhatsApp mesajı gönderiyor
        console.log('Application submitted successfully. Email and WhatsApp sent by backend.');
        
        setShowSuccess(true);
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          position: '',
          message: ''
        });
        setSelectedFile(null);
        setFileError('');
      }
    } catch (error) {
      console.error('Error:', error);
      alert(t('careers.error'));
    }
  };

  return (
    <section id="careers" className="careers-section">
      <Container>
        <Row className="align-items-center">
          <Col md={6}>
            <motion.div
              className="careers-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={fadeOnly}
            >
              <h2>{t('careers.title')}</h2>
              <p>
                {t('careers.description')}
              </p>
            </motion.div>
          </Col>
          <Col md={6}>
            <motion.div
              className="careers-form"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={scaleIn}
              transition={{ delay: 0.2 }}
            >
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.full_name')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        placeholder={t('careers.form.full_name_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.email')}</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('careers.form.email_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.phone')}</Form.Label>
                      <Form.Control 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('careers.form.phone_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.position')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        placeholder={t('careers.form.position_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.message')}</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('careers.form.message_placeholder')} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('careers.form.file')}</Form.Label>
                      <Form.Control 
                        type="file" 
                        onChange={handleFileChange}
                        accept=".jpg,.jpeg,.png,.pdf"
                        className={fileError ? 'is-invalid' : ''}
                      />
                      {selectedFile && (
                        <div className="mt-2">
                          <small className="text-success">
                            ✅ {t('careers.form.file_selected')} {selectedFile.name}
                          </small>
                        </div>
                      )}
                      {fileError && (
                        <div className="mt-2">
                          <small className="text-danger">
                            ❌ {fileError}
                          </small>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        {t('careers.form.file_help')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12} className="d-flex justify-content-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                    >
                      {t('careers.form.submit')}
                    </button>
                  </Col>
                </Row>
              </Form>
            </motion.div>
          </Col>
        </Row>
      </Container>
      
      {/* Success Modal */}
      <Modal 
        show={showSuccess} 
        onHide={() => setShowSuccess(false)}
        centered
      >
        <Modal.Body className="text-center p-4">
          <FaCheckCircle size={50} color="#28a745" className="mb-3" />
          <h4>{t('careers.success.title')}</h4>
          <p>{t('careers.success.message')}</p>
          <button 
            className="btn btn-success mt-3" 
            onClick={() => setShowSuccess(false)}
          >
            {t('careers.success.close')}
          </button>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default CareersPage;